import type { Anomaly } from '@soc/shared';
import { sevChip, sevColor } from '../../lib/severity';
import { formatTimestamp } from '../../lib/format';
import { RuleIcon } from '../../lib/ruleIcons';
import { useInvestigateAlert } from '../map/useInvestigateAlert';

export function AlertCard({
  alert,
  highlighted = false,
  registerRef,
}: {
  alert: Anomaly;
  highlighted?: boolean;
  registerRef?: (el: HTMLDivElement | null) => void;
}) {
  const color = sevColor(alert.severity);
  const { canInvestigate, investigate } = useInvestigateAlert(alert);

  return (
    <div
      ref={registerRef}
      style={{
        display: 'flex',
        gap: 11,
        padding: '11px 12px',
        background: 'var(--surface-card)',
        border: '1px solid var(--border-card)',
        borderLeft: `3px solid ${color}`,
        borderRadius: 9,
        opacity: alert.acknowledged ? 0.5 : 1,
        boxShadow: highlighted ? `0 0 0 2px ${color}, 0 0 14px ${color}` : undefined,
        transition: 'box-shadow .25s',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 30,
          height: 30,
          flex: 'none',
          borderRadius: 8,
          color,
          background: sevChip(alert.severity),
          border: `1px solid ${color}`,
        }}
      >
        <RuleIcon ruleId={alert.ruleId} size={17} />
      </span>
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 5 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span
            className="eyebrow"
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.8,
              color,
              background: sevChip(alert.severity),
              padding: '2px 6px',
              borderRadius: 4,
              flex: 'none',
            }}
          >
            {alert.severity}
          </span>
          <span
            style={{
              flex: 1,
              fontSize: 13.5,
              fontWeight: 600,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {alert.ruleName}
          </span>
          {/* Timestamp rides the header row so the body columns get the full card
              width — on a narrow panel a right-pinned timestamp starved them. */}
          <span
            className="mono"
            style={{ fontSize: 11, color: 'var(--text-dim)', whiteSpace: 'nowrap', flex: 'none' }}
          >
            {formatTimestamp(alert.timestamp)}
          </span>
        </div>
        <div
          className="mono"
          style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0, fontSize: 12, color: 'var(--text-muted)' }}
        >
          <span
            style={{
              color: 'var(--text-secondary)',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {alert.towerId}
          </span>
        </div>
        <span
          className="mono"
          style={{
            fontSize: 12,
            color,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {alert.value}
        </span>
        {canInvestigate && (
          <button
            type="button"
            onClick={investigate}
            title="Open investigation"
            style={{
              alignSelf: 'flex-start',
              marginTop: 2,
              padding: '3px 9px',
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--text-secondary)',
              background: 'var(--surface-app)',
              border: '1px solid var(--border-hover)',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            🔍 Investigate
          </button>
        )}
      </div>
    </div>
  );
}
